import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  TrendingUp, BarChart2, PieChart, Users, Package, CreditCard,
  FileText, Download, Calendar, Filter, Search, Plus, Loader2, Check
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function SalesReportsPage() {
  const [dateRange, setDateRange] = useState('30')
  const [reportType, setReportType] = useState('sales')

  // Sample report data
  const salesByDay = Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toLocaleDateString('fa-IR'),
    sales: Math.floor(Math.random() * 50000) + 20000,
    quantity: Math.floor(Math.random() * 100) + 30,
    customers: Math.floor(Math.random() * 30) + 10,
  }))

  const paymentMethods = [
    { method: 'نقدی', count: 45, amount: 780000, color: 'primary' },
    { method: 'کارت به کارت', count: 28, amount: 560000, color: 'success' },
    { method: 'انتقال بانکی', count: 15, amount: 300000, color: 'info' },
    { method: 'پاس', count: 12, amount: 240000, color: 'warning' },
  ]

  const topProducts = [
    { name: 'انرژی نوشابه ۳۳۰ml', sales: 48000, quantity: 96, revenue: 576000, image: '/placeholder.svg' },
    { name: 'چیپس ذرتی ۱۰۰g', sales: 35000, quantity: 50, revenue: 350000, image: '/placeholder.svg' },
    { name: 'بستنی یونانی ۱۵۰g', sales: 32000, quantity: 60, revenue: 432000, image: '/placeholder.svg' },
    { name: 'سیگارت ۲۲ عددی', sales: 28000, quantity: 28, revenue: 392000, image: '/placeholder.svg' },
    { name: 'پاستیل ۵۰۰g', sales: 22000, quantity: 44, revenue: 308000, image: '/placeholder.svg' },
  ]

  const shiftPerformance = [
    { shift: 'شیفت ۱', sales: 125000, orders: 28, avgOrder: 4464, status: 'completed' },
    { shift: 'شیفت ۲', sales: 98000, orders: 22, avgOrder: 4455, status: 'completed' },
    { shift: 'شیفت ۳', sales: 156000, orders: 35, avgOrder: 4457, status: 'completed' },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">گزارشات فروش</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            تحلیل و آمار فروش روزانه، محصولی، و عملکردی
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={dateRange}
            onChange={(v) => setDateRange(v || '30')}
            options={[
              { value: '7', label: '۷ روز' },
              { value: '30', label: '۳۰ روز' },
              { value: '90', label: '۹۰ روز' },
              { value: '365', label: '۱ سال' },
            ]}
            placeholder="بازه زمان"
            className="w-32"
          />
          <Select
            value={reportType}
            onChange={(v) => setReportType(v || 'sales')}
            options={[
              { value: 'sales', label: 'فروش' },
              { value: 'products', label: 'محصولات' },
              { value: 'customers', label: 'مشتریان' },
              { value: 'payments', label: 'پرداخت‌ها' },
            ]}
            placeholder="نوع گزارش"
            className="w-32"
          />
          <Dropdown
            trigger={<Button variant="outline"><Download className="w-4 h-4 ml-1" /> خروجی گزارش</Button>}
            items={[
              { label: 'خروجی Excel', icon: FileText, onClick: () => {} },
              { label: 'خروجی CSV', icon: FileText, onClick: () => {} },
              { type: 'divider' },
              { label: 'اعلان PDF', icon: FileText, onClick: () => {} },
            ]}
          />
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {[
          { label: 'کل فروشت', value: formatCurrency(1456000), trend: '+12.5%', trendColor: 'success', icon: TrendingUp },
          { label: 'تعداد فروش', value: formatNumber(324), trend: '+8.2%', trendColor: 'success', icon: BarChart2 },
          { label: 'مشتری‌های فعال', value: formatNumber(98), trend: '+15.3%', trendColor: 'success', icon: Users },
          { label: 'میانگین سفارش', value: formatCurrency(4495), trend: '-2.1%', trendColor: 'warning', icon: CreditCard },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', `bg-${stat.trendColor}-100 dark:bg-${stat.trendColor}-900/30`)}>
                <stat.icon className={cn('w-6 h-6', `text-${stat.trendColor}-600 dark:text-${stat.trendColor}-400`)} />
              </div>
              <div className={cn('text-sm font-medium', stat.trendColor === 'success' ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400')}>{stat.trend}</div>
            </div>
            <h3 className="text-sm text-neutral-500 dark:text-neutral-400 font-medium mb-1">{stat.label}</h3>
            <div className="text-2xl font-bold text-neutral-900 dark:text-white">{stat.value}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Chart Placeholder */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6"
      >
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">روند روزانه فروش</h2>
        <div className="h-80 flex items-end justify-between gap-2">
          {salesByDay.slice(-10).map((day, i) => {
            const maxValue = Math.max(...salesByDay.map(d => d.sales))
            const height = (day.sales / maxValue) * 100

            return (
              <motion.div
                key={day.date}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: `${height}%` }}
                transition={{ delay: 0.1 + i * 0.05, duration: 0.5 }}
                className="flex-1 flex flex-col items-center gap-2"
              >
                <div className="w-full bg-gradient-to-t from-primary-500/20 to-primary-500 rounded-t-lg transition-all hover:from-primary-600/30" style={{ height: `${height}%`, minHeight: '20%' }} />
                <span className="text-xs text-neutral-500 dark:text-neutral-400">{day.date.split('/')[1]}</span>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">روش‌های پرداخت</h2>
          <div className="space-y-4">
            {paymentMethods.map((method, i) => (
              <div key={method.method} className="flex items-center justify-between py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-${method.color}-500` } />
                  <span className="text-neutral-900 dark:text-white font-medium">{method.method}</span>
                </div>
                <div className="text-left">
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">{method.count} تراکنش</div>
                  <div className="font-mono text-neutral-900 dark:text-white">{formatCurrency(method.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Top Products */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">پرفروش‌ترین محصولات</h2>
          <div className="space-y-4">
            {topProducts.slice(0, 5).map((product, i) => (
              <div key={product.name} className="flex items-center gap-4 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <Package className="w-5 h-5 text-neutral-400" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-neutral-900 dark:text-white">{product.name}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">{product.quantity} دستگاه × {formatCurrency(product.price || 0)}</div>
                </div>
                <div className="text-left">
                  <div className="font-mono text-neutral-900 dark:text-white">{formatCurrency(product.revenue)}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">{product.sales} تومان فروشت</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Shift Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card p-6 lg:col-span-2"
        >
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">عملکرد شیفت‌ها</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-right pb-3 text-neutral-500 dark:text-neutral-400 font-medium">شیفت</th>
                  <th className="text-right pb-3 text-neutral-500 dark:text-neutral-400 font-medium">فروشت (تومان)</th>
                  <th className="text-right pb-3 text-neutral-500 dark:text-neutral-400 font-medium">تعداد سفارش</th>
                  <th className="text-right pb-3 text-neutral-500 dark:text-neutral-400 font-medium">میانگین سفارش</th>
                  <th className="text-right pb-3 text-neutral-500 dark:text-neutral-400 font-medium">وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {shiftPerformance.map((shift, i) => (
                  <tr key={shift.shift} className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="py-3 font-medium text-neutral-900 dark:text-white">{shift.shift}</td>
                    <td className="py-3 font-mono text-neutral-900 dark:text-white">{formatCurrency(shift.sales)}</td>
                    <td className="py-3 text-neutral-700 dark:text-neutral-300">{shift.orders}</td>
                    <td className="py-3 font-mono text-neutral-900 dark:text-white">{formatCurrency(shift.avgOrder)}</td>
                    <td className="py-3">
                      <Badge variant="success" className="text-xs">
                        <Check className="w-3 h-3 ml-1" />
                        {shift.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  )
}